'use client';

import React from 'react';
import { TrendingUp, TrendingDown, Bitcoin, Layers, Activity } from 'lucide-react';
import type { GlobalMarketData } from '@/hooks/useMarketAnalysis';

interface FearGreedData {
  value: number;
  classification: string;
}

interface GlobalMarketStatsProps {
  data: GlobalMarketData;
  fearGreed?: FearGreedData;
  className?: string;
  variant?: 'default' | 'compact' | 'inline';
}

/**
 * Circular Fear & Greed Meter Component
 */
function FearGreedMeter({ value, classification }: FearGreedData) {
  const size = 56;
  const strokeWidth = 5;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = (value / 100) * circumference;

  // Get color based on value (using CSS classes that work in both light and dark modes)
  const getColor = () => {
    if (value <= 25) return { fill: '#10b981', text: 'text-emerald-600 dark:text-emerald-400', label: 'Extreme Fear' };
    if (value <= 45) return { fill: '#22c55e', text: 'text-green-600 dark:text-green-400', label: 'Fear' };
    if (value <= 55) return { fill: '#737373', text: 'text-neutral-600 dark:text-neutral-400', label: 'Neutral' };
    if (value <= 75) return { fill: '#f97316', text: 'text-orange-600 dark:text-orange-400', label: 'Greed' };
    return { fill: '#f43f5e', text: 'text-rose-600 dark:text-rose-400', label: 'Extreme Greed' };
  };

  const getDescription = () => {
    if (value <= 25) return 'Historically a buying opportunity';
    if (value <= 45) return 'Investors are cautious';
    if (value <= 55) return 'No strong directional bias';
    if (value <= 75) return 'Optimism is high';
    return 'Historically signals caution';
  };

  const colors = getColor();

  return (
    <div className="flex items-center gap-3">
      {/* Circular Gauge */}
      <div className="relative flex items-center justify-center">
        <svg width={size} height={size} className="transform -rotate-90">
          {/* Background circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            className="stroke-neutral-200 dark:stroke-neutral-800"
            strokeWidth={strokeWidth}
          />
          {/* Progress arc */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={colors.fill}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={circumference - progress}
            className="transition-all duration-500 ease-out"
          />
        </svg>
        {/* Center value */}
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`font-mono text-sm font-bold ${colors.text}`}>
            {value}
          </span>
        </div>
      </div>
      
      {/* Labels */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] text-neutral-500 uppercase tracking-wider">Fear & Greed</span>
        </div>
        <p className={`font-mono text-xs font-bold ${colors.text}`}>
          {colors.label}
        </p>
        <p className="font-mono text-[9px] text-neutral-500 mt-0.5">
          {getDescription()}
        </p>
      </div>
    </div>
  );
}

/**
 * Global Market Stats Component
 * Displays CoinGecko global market data (total market cap, BTC dominance, etc.)
 * Optionally includes Fear & Greed circular meter
 */
export default function GlobalMarketStats({ 
  data,
  fearGreed,
  className = '',
  variant = 'default'
}: GlobalMarketStatsProps) {
  
  // Format large numbers
  const formatNumber = (num: number): string => {
    if (num >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
    if (num >= 1e9) return `$${(num / 1e9).toFixed(0)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(0)}M`;
    return `$${num.toLocaleString()}`;
  };

  const isPositive = data.marketCapChange24h >= 0;

  // Inline variant - just key stats in a row
  if (variant === 'inline') {
    return (
      <div className={`flex items-center gap-3 text-xs font-mono ${className}`}>
        <span className="text-neutral-500">Global:</span>
        <span className={isPositive ? 'text-emerald-400' : 'text-rose-400'}>
          {isPositive ? '+' : ''}{data.marketCapChange24h.toFixed(1)}%
        </span>
        <span className="text-neutral-600">•</span>
        <span className="text-amber-400">BTC {data.btcDominance.toFixed(1)}%</span>
      </div>
    );
  }

  // Compact variant - horizontal stats bar
  if (variant === 'compact') {
    return (
      <div className={`flex items-center justify-between gap-4 px-3 py-2 bg-neutral-100 dark:bg-neutral-900/50 border border-neutral-200 dark:border-neutral-800 rounded-lg ${className}`}>
        <div className="flex items-center gap-2">
          <Activity size={12} className="text-neutral-500" />
          <span className="font-mono text-[10px] text-neutral-500 uppercase">Global</span>
        </div>
        <div className="flex items-center gap-3 text-xs font-mono">
          <span className={`flex items-center gap-1 ${isPositive ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
            {isPositive ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
            {isPositive ? '+' : ''}{data.marketCapChange24h.toFixed(1)}%
          </span>
          <span className="text-neutral-300 dark:text-neutral-600">|</span>
          <span className="text-amber-600 dark:text-amber-400">BTC {data.btcDominance.toFixed(1)}%</span>
          <span className="text-blue-600 dark:text-blue-400">ETH {data.ethDominance.toFixed(1)}%</span>
        </div>
      </div>
    );
  }

  // Default variant - full stats card
  return (
    <div className={`bg-neutral-100 dark:bg-neutral-900/50 border border-neutral-200 dark:border-neutral-800 rounded-lg p-3 ${className}`}>
      {/* Fear & Greed Meter - at the top if provided */}
      {fearGreed && (
        <div className="mb-3 pb-3 border-b border-neutral-200 dark:border-neutral-800">
          <FearGreedMeter value={fearGreed.value} classification={fearGreed.classification} />
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Activity size={12} className="text-neutral-500" />
          <span className="font-mono text-[10px] text-neutral-500 uppercase tracking-wider">
            Global Market
          </span>
        </div>
        <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono font-bold ${
          isPositive 
            ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-800/50' 
            : 'bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-400 border border-rose-300 dark:border-rose-800/50'
        }`}>
          {isPositive ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
          {isPositive ? '+' : ''}{data.marketCapChange24h.toFixed(1)}%
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-2">
        {/* Total Market Cap */}
        <div className="space-y-0.5">
          <span className="font-mono text-[9px] text-neutral-500 uppercase">Market Cap</span>
          <p className="font-mono text-sm font-bold text-neutral-900 dark:text-white">
            {formatNumber(data.totalMarketCap)}
          </p>
        </div>

        {/* 24h Volume */}
        <div className="space-y-0.5">
          <span className="font-mono text-[9px] text-neutral-500 uppercase">24h Volume</span>
          <p className="font-mono text-sm font-bold text-neutral-700 dark:text-neutral-300">
            {formatNumber(data.totalVolume24h)}
          </p>
        </div>

        {/* BTC Dominance */}
        <div className="space-y-0.5">
          <span className="font-mono text-[9px] text-neutral-500 uppercase flex items-center gap-1">
            <Bitcoin size={9} className="text-amber-500" />
            BTC Dominance
          </span>
          <p className="font-mono text-sm font-bold text-amber-600 dark:text-amber-400">
            {data.btcDominance.toFixed(1)}%
          </p>
        </div>

        {/* ETH Dominance */}
        <div className="space-y-0.5">
          <span className="font-mono text-[9px] text-neutral-500 uppercase flex items-center gap-1">
            <Layers size={9} className="text-blue-500 dark:text-blue-400" />
            ETH Dominance
          </span>
          <p className="font-mono text-sm font-bold text-blue-600 dark:text-blue-400">
            {data.ethDominance.toFixed(1)}%
          </p>
        </div>
      </div>

      {/* BTC Dominance Interpretation */}
      {data.btcDominance > 55 && (
        <p className="mt-2 font-mono text-[9px] text-amber-600/70 italic">
          High BTC dominance → risk-off sentiment
        </p>
      )}
      {data.btcDominance < 45 && (
        <p className="mt-2 font-mono text-[9px] text-emerald-600/70 italic">
          Low BTC dominance → altcoin season
        </p>
      )}
    </div>
  );
}

